import { FiltersIntegrationName, AccIntegrationName, LocationIntegrationName } from '@constants/integrations';
import { compareAsc, parseISO } from 'date-fns';

export const prepareReviewsAndFeedbackOverall = data => {
    let overall = data;
    if (Array.isArray(overall)) {
        overall = data.pop();
    }

    if (overall.totalReviewsDelta) {
        overall.totalReviewsDelta = overall.totalReviewsDelta.toFixed(0);
    }
    if (overall.totalResponsesDelta) {
        overall.totalResponsesDelta = overall.totalResponsesDelta.toFixed(0);
    }
    if (overall.netPromoterScoreDelta) {
        overall.netPromoterScoreDelta = overall.netPromoterScoreDelta.toFixed(0);
    }
    if (overall.negativeResponsesDelta) {
        overall.negativeResponsesDelta = overall.negativeResponsesDelta.toFixed(0);
    }

    return overall;
};

export function checkIntegration(i) {
    const mainIntegrations = [
        FiltersIntegrationName.Facebook,
        AccIntegrationName.Facebook,
        FiltersIntegrationName.Google,
        AccIntegrationName.Google,
        FiltersIntegrationName['Booking.com'],
        FiltersIntegrationName.TheFork,
        FiltersIntegrationName.Deliveroo,
        FiltersIntegrationName.Trustpilot,
        FiltersIntegrationName.iFood,
        FiltersIntegrationName['Hotels.com'],
        FiltersIntegrationName.Expedia,
    ];

    const isOthers = Object.keys(i).reduce((sum, key) => {
        return mainIntegrations.includes(+key) ? sum : sum + i[key];
    }, 0);

    return {
        isGoogle: (i[FiltersIntegrationName.Google] || 0) + (i[AccIntegrationName.Google] || 0),
        isFacebook: (i[FiltersIntegrationName.Facebook] || 0) + (i[AccIntegrationName.Facebook] || 0),
        isBooking: i[FiltersIntegrationName['Booking.com']] || 0,
        isTrustpilot: i[FiltersIntegrationName.Trustpilot] || 0,
        isIFood: i[FiltersIntegrationName.iFood] || 0,
        isTheFork: i[FiltersIntegrationName.TheFork] || 0,
        isDeliveroo: i[FiltersIntegrationName.Deliveroo] || 0,
        isHotels: i[FiltersIntegrationName['Hotels.com']] || 0,
        isExpedia: i[FiltersIntegrationName.Expedia] || 0,
        isOthers: isOthers,
    };
}

export const prepareSentimentData = data => {
    return data
        .map(item => {
            return {
                averageRating: item.breakdown[0].averageRating.toFixed(2),
                numberOfNegativeReviews: item.breakdown[0].numberOfNegativeReviews,
                numberOfPositiveReviews: item.breakdown[0].numberOfPositiveReviews,
                dateLabel: parseISO(item.date),
            };
        })
        .sort((a, b) => compareAsc(a.dateLabel, b.dateLabel));
};

export const prepareNpsData = data => {
    return data
        .map(item => {
            return {
                promoters: item.breakdown[0].promoters,
                neutrals: item.breakdown[0].neutrals,
                detractors: item.breakdown[0].detractors,
                nps: item.breakdown[0].nps?.toFixed(2),
                dateLabel: parseISO(item.date),
            };
        })
        .sort((a, b) => compareAsc(a.dateLabel, b.dateLabel));
};

export const prepareLocationConversion = location => {
    return {
        id: location.locationId,
        surveyTemplateId: location.surveyTemplateId,
        // qr distribution
        sentSurveys: location.sentSurveys,
        sentSurveysPercent: location.sentSurveysPercent?.toFixed(2),

        openedSurveys: location.openedSurveys,
        openedSurveysPercent: location.openedSurveysPercent.toFixed(2),
        openedSurveysChange: location.openedSurveysChange,

        responsesReceived: location.responsesReceived,
        responsesReceivedPercent: location.responsesReceivedPercent.toFixed(2),
        responsesReceivedChange: location.responsesReceivedChange,

        leaveReviewClicked: location.leaveReviewClicked,
        leaveReviewClickedPercent: location.leaveReviewClickedPercent.toFixed(2),
        leaveReviewClickedChange: location.leaveReviewClickedChange,

        reviewsReceived: location.reviewsReceived,
        reviewsReceivedChange: location.reviewsReceivedChange,
    };
};

export const prepareConversionTotal = company => {
    return {
        sentSurveys: company.sentSurveys,

        openedSurveys: company.openedSurveys,
        openedSurveysPercent: company.openedSurveysPercent.toFixed(2),

        responsesReceived: company.responsesReceived,
        responsesReceivedPercent: company.responsesReceivedPercent.toFixed(2),

        leaveReviewClicked: company.leaveReviewClicked,
        leaveReviewClickedPercent: company.leaveReviewClickedPercent.toFixed(2),

        reviewsReceived: company.reviewsReceived,
    };
};

export function getPresenceOptions(companies, selectedCompanies, withApple = false) {
    const { googleConnected, facebookConnected, appleConnected } = (companies ?? [])
        .filter(c => selectedCompanies.includes(c.companyId))
        .flatMap(c => c.profiles ?? [])
        .reduce(
            (prev, acc) => ({
                ...prev,
                googleConnected: prev.googleConnected || acc.type === LocationIntegrationName.Google,
                facebookConnected: prev.facebookConnected || acc.type === LocationIntegrationName.Facebook,
                appleConnected: prev.appleConnected || acc.type === LocationIntegrationName.Apple,
            }),
            { googleConnected: false, facebookConnected: false, appleConnected: false },
        );

    const platformsConnectedIds = [
        ...(googleConnected ? [LocationIntegrationName.Google] : []),
        ...(facebookConnected ? [LocationIntegrationName.Facebook] : []),
        ...(appleConnected && withApple ? [LocationIntegrationName.Apple] : []),
    ];

    const platformsOptions = [
        ...(googleConnected ? [{ value: LocationIntegrationName.Google, label: 'Google' }] : []),
        ...(facebookConnected ? [{ value: LocationIntegrationName.Facebook, label: 'Facebook' }] : []),
        ...(appleConnected && withApple ? [{ value: LocationIntegrationName.Apple, label: 'Apple' }] : []),
    ];

    return { platformsConnectedIds, platformsOptions };
}
